/**
 * Frontend consumer types for the Owner Support Case Management API (032).
 *
 * All types mirror backend contracts from `support-api.yaml` and `open-api.json`.
 * All interfaces are readonly — mutation is driven through hook mutations returning fresh data.
 *
 * Feature: 032-booking-recovery-linking
 */

// ─── SupportCaseStatus ────────────────────────────────────────────────────────

export type SupportCaseStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'ABANDONED';

/** Non-final statuses drive the badge count and default list filter. */
export const NON_FINAL_SUPPORT_CASE_STATUSES: ReadonlySet<SupportCaseStatus> = new Set([
  'OPEN',
  'IN_PROGRESS',
] as const);

// ─── SupportCaseReason ────────────────────────────────────────────────────────

export type SupportCaseReason =
  | 'EMAIL_NOT_RECEIVED'
  | 'TOKEN_EXPIRED'
  | 'OFFLINE_PAYMENT_DEPOSIT'
  | 'OFFLINE_PAYMENT_FULL'
  | 'OTHER';

// ─── SupportActionType ────────────────────────────────────────────────────────

export type SupportActionType =
  | 'RESEND_PAYMENT_ACCESS'        // booking must have ACTIVE secure token
  | 'RESET_EXPIRED_ACCESS'         // booking must have EXPIRED secure token + recoverable status
  | 'RECORD_OFFLINE_DEPOSIT'       // booking must be AWAITING_GUEST_PAYMENT
  | 'RECORD_OFFLINE_FULL_PAYMENT'; // booking must be AWAITING_GUEST_PAYMENT or DEPOSIT_PAID_AWAITING_BALANCE

/** Actions that require expectedBookingVersion for optimistic-lock safety. */
export const VERSION_REQUIRED_ACTIONS: ReadonlySet<SupportActionType> = new Set([
  'RECORD_OFFLINE_DEPOSIT',
  'RECORD_OFFLINE_FULL_PAYMENT',
] as const);

// ─── SupportActionStatus ──────────────────────────────────────────────────────

export type SupportActionStatus = 'PENDING' | 'SUCCEEDED' | 'REJECTED' | 'FAILED';

// ─── SupportOutcome ───────────────────────────────────────────────────────────

export type SupportOutcome =
  | 'SUCCESS'
  | 'NOT_ALLOWED'
  | 'ALREADY_COMPLETED'
  | 'STALE_UPDATE'
  | 'NOT_FOUND';

// ─── SupportCase ─────────────────────────────────────────────────────────────

/** Mirrors SupportCaseResponse from support-api.yaml. */
export interface SupportCase {
  readonly caseId: string;
  readonly bookingId: string;
  readonly status: SupportCaseStatus;
  readonly reason: SupportCaseReason;
  readonly actorId: string;
  readonly actorType: 'OWNER' | 'ADMIN';
  readonly note: string | null;
  readonly openedAt: string;        // ISO 8601
  readonly resolvedAt: string | null;
}

// ─── SupportCaseDetail ───────────────────────────────────────────────────────

/** Mirrors SupportCaseDetailResponse (extends SupportCaseResponse with actions[]). */
export interface SupportCaseDetail extends SupportCase {
  readonly actions: ReadonlyArray<SupportCaseAction>;
}

// ─── SupportCaseAction ───────────────────────────────────────────────────────

/** Mirrors SupportActionResponse. */
export interface SupportCaseAction {
  readonly actionId: string;
  readonly caseId: string;
  readonly actionType: SupportActionType;
  readonly status: SupportActionStatus;
  readonly outcome: SupportOutcome | null;
  readonly outcomeNote: string | null;
  readonly bookingVersion: number | null;  // non-null on SUCCESS
  readonly performedAt: string;            // ISO 8601
  readonly outcomeAt: string | null;
}

// ─── Request types ────────────────────────────────────────────────────────────

export interface OpenSupportCaseRequest {
  readonly bookingId: string;
  readonly reason: SupportCaseReason;
  readonly note?: string;  // max 500 chars
}

export interface SubmitSupportActionRequest {
  readonly actionType: SupportActionType;
  readonly idempotencyKey: string;           // UUID v4 generated client-side
  readonly expectedBookingVersion?: number;  // required for RECORD_OFFLINE_* actions
  readonly note?: string;                    // max 500 chars
}

// ─── Timeline ────────────────────────────────────────────────────────────────

/** Origin values for a BookingTimeline entry. */
export type TimelineEntryOrigin = 'SYSTEM' | 'OWNER' | 'SUPPORT' | 'SCHEDULER';

/** Mirrors a single entry in BookingTimelineResponse.entries. */
export interface TimelineEntry {
  readonly eventType: string;
  readonly origin: TimelineEntryOrigin;
  readonly actorId: string | null;
  readonly actionType: SupportActionType | null;
  readonly outcome: SupportOutcome | null;
  readonly previousBookingStatus: string | null;
  readonly previousBookingVersion: number | null;
  readonly resultingBookingStatus: string | null;
  readonly resultingBookingVersion: number | null;
  readonly occurredAt: string;    // ISO 8601; entries are ordered ascending
  readonly context: string | null; // JSON string with event-specific metadata
}

export interface BookingTimelineResponse {
  readonly bookingId: string;
  readonly entries: ReadonlyArray<TimelineEntry>;
}

// ─── Deferred booking inquiry (B2 contract) ───────────────────────────────────

export type DeferredInquiryDecisionStatus = 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';

export interface OwnerDeferredBookingInquiry {
  readonly inquiryId: string;
  readonly bookingId: string | null;     // null when no booking was created (DECLINED/EXPIRED/PENDING)
  readonly apartmentId: string;
  readonly decisionStatus: DeferredInquiryDecisionStatus;
  readonly currentBookingStatus: string | null;
  readonly bookingSource: string;        // safe fallback — not strict BookingSource
  readonly guest: {
    readonly fullName: string;
    readonly email: string;
  };
  readonly stay: {
    readonly checkInDate: string;
    readonly checkOutDate: string;
  };
  readonly createdAt: string;
  readonly ownerReviewExpiresAt: string;
  readonly decisionAt: string | null;
  readonly declineReason: string | null;
}

export interface OwnerDeferredBookingInquiryPage {
  readonly content: ReadonlyArray<OwnerDeferredBookingInquiry>;
  readonly page: number;
  readonly size: number;
  readonly totalElements: number;
  readonly totalPages: number;
}
